'use client';

import { formatCredits } from '@/@credit-consumption/utils/formatCredits';
import type { PauseCopy } from '@/lib/credits/credit-vocabulary';
import { Button, Link } from '@heroui/react';
import { Icon } from '@iconify/react';
import { format, parseISO } from 'date-fns';
import NextLink from 'next/link';
import { useEffect, useState } from 'react';

import { useStudioCreditAlerts } from '../hooks/useStudioCreditAlerts';
import { userSpendLimitChatText } from '../utils/user-spend-limit-copy';

interface StudioAICreditBannersProps {
    compact?: boolean;
    /** Top: approaching-limit warnings. Bottom: paused/capped indicator near chat input. */
    placement?: 'top' | 'bottom' | 'all';
}

/** The pace warning stays dismissed for the rest of the day (as the trial banner did). */
function paceDismissalKey(): string {
    return `credits_pace_warning_dismissed_${new Date().toISOString().slice(0, 10)}`;
}

function readPaceDismissed(): boolean {
    try {
        return localStorage.getItem(paceDismissalKey()) === 'true';
    } catch {
        return false;
    }
}

function writePaceDismissed(): void {
    try {
        localStorage.setItem(paceDismissalKey(), 'true');
    } catch {
        // Private windows and blocked storage: dismissed for this page only.
    }
}

interface LedgerBannerStyle {
    textSize: string;
    subTextSize: string;
    padding: string;
    iconSize: number;
}

/**
 * The pause banner for an org on the new balance (M4S-5895 step 27, credits-ui.md §7 "Chat"):
 * the reason and next step from `pauseCopy`, ending "See credits →" to the Credits tab.
 */
function LedgerPauseBanner({ copy, style }: { copy: PauseCopy; style: LedgerBannerStyle }) {
    return (
        <div
            className={`flex items-start gap-3 rounded-xl border border-danger-200 bg-danger-50 ${style.padding}`}
            role="status"
        >
            <Icon
                icon="lucide:pause-circle"
                className="mt-0.5 shrink-0 text-danger"
                width={style.iconSize}
                height={style.iconSize}
            />
            <div className="min-w-0 flex-1">
                <p className={`${style.textSize} font-semibold text-danger-800`}>{copy.title}</p>
                {(copy.body || copy.link) && (
                    <p className={`mt-0.5 ${style.subTextSize} text-danger-600`}>
                        {copy.body}
                        {copy.link && (
                            <>
                                {copy.body ? ' ' : ''}
                                <Link
                                    as={NextLink}
                                    href={copy.link.href}
                                    size="sm"
                                    underline="always"
                                    className={`${style.subTextSize} font-semibold text-danger-700`}
                                >
                                    {copy.link.label} →
                                </Link>
                            </>
                        )}
                    </p>
                )}
            </div>
        </div>
    );
}

/** The dismissible pace warning that replaces the monthly one on the new balance. */
function LedgerPaceWarning({ text, style }: { text: string; style: LedgerBannerStyle }) {
    const [dismissed, setDismissed] = useState(true);
    useEffect(() => setDismissed(readPaceDismissed()), []);
    if (dismissed) return null;
    return (
        <div
            className={`flex items-start gap-3 rounded-xl border border-warning-200 bg-warning-50 ${style.padding}`}
            role="status"
        >
            <Icon
                icon="lucide:trending-down"
                className="mt-0.5 shrink-0 text-warning"
                width={style.iconSize}
                height={style.iconSize}
            />
            <p className={`min-w-0 flex-1 ${style.textSize} font-semibold text-warning-800`}>
                {text}
            </p>
            <Button
                isIconOnly
                size="sm"
                variant="light"
                aria-label="Dismiss"
                className="h-6 w-6 min-w-6 text-warning-700"
                onPress={() => {
                    writePaceDismissed();
                    setDismissed(true);
                }}
            >
                <Icon icon="lucide:x" width={14} height={14} />
            </Button>
        </div>
    );
}

/**
 * Approaching-limit (amber) and cap-hit (red) banners for Studio AI chat.
 * Shown for org-trial users subject to the credit gate. An org on the new balance gets its
 * pause from `pauseCopy` and the pace warning; every other org keeps today's banners.
 */
export function StudioAICreditBanners({
    compact = false,
    placement = 'all',
}: StudioAICreditBannersProps) {
    const {
        showAlerts,
        reader,
        pause,
        paceWarning,
        isPaused,
        pauseReason,
        userSpendLimit,
        userMonthToDate,
        resetDate,
        licenseEndDate,
        isDaniWarning,
        isPoolWarning,
        summary,
        creditsRemaining,
        estimatedPct,
        poolPct,
    } = useStudioCreditAlerts();

    const showPaused = isPaused && (placement === 'bottom' || placement === 'all');
    const showWarnings =
        (isDaniWarning || isPoolWarning) && (placement === 'top' || placement === 'all');

    if (!showAlerts) return null;

    if (reader === 'ledger') {
        const style: LedgerBannerStyle = {
            textSize: compact ? 'text-tiny' : 'text-small',
            subTextSize: compact ? 'text-tiny' : 'text-mini',
            padding: compact ? 'px-3 py-2.5' : 'px-4 py-3',
            iconSize: compact ? 16 : 18,
        };
        const showPace = !!paceWarning && (placement === 'top' || placement === 'all');
        if (!(showPaused && pause) && !showPace) return null;
        return (
            <div className="flex flex-col gap-2">
                {showPaused && pause && <LedgerPauseBanner copy={pause} style={style} />}
                {showPace && <LedgerPaceWarning text={paceWarning} style={style} />}
            </div>
        );
    }

    if (!showPaused && !showWarnings) return null;

    const resetLabel = resetDate ? format(parseISO(resetDate), 'MMM d') : 'next reset';
    const licenseEndLabel = licenseEndDate ? format(parseISO(licenseEndDate), 'MMM d, yyyy') : null;
    const textSize = compact ? 'text-tiny' : 'text-small';
    const subTextSize = compact ? 'text-tiny' : 'text-mini';
    const padding = compact ? 'px-3 py-2.5' : 'px-4 py-3';
    const iconSize = compact ? 16 : 18;

    return (
        <div className="flex flex-col gap-2">
            {showPaused && (
                <div
                    className={`flex items-start gap-3 rounded-xl border border-danger-200 bg-danger-50 ${padding}`}
                >
                    <Icon
                        icon="lucide:pause-circle"
                        className="mt-0.5 shrink-0 text-danger"
                        width={iconSize}
                        height={iconSize}
                    />
                    <div className="min-w-0 flex-1">
                        {pauseReason === 'license_expired' ? (
                            <>
                                <p className={`${textSize} font-semibold text-danger-800`}>
                                    Studio AI license ended
                                    {licenseEndLabel ? ` on ${licenseEndLabel}` : ''}.
                                </p>
                                <p className={`mt-0.5 ${subTextSize} text-danger-600`}>
                                    DANI queries are paused. Contact your account team to renew your
                                    license.
                                </p>
                            </>
                        ) : pauseReason === 'user_spend_limit_exceeded' ? (
                            <>
                                <p className={`${textSize} font-semibold text-danger-800`}>
                                    Monthly AI credit limit reached.
                                </p>
                                <p className={`mt-0.5 ${subTextSize} text-danger-600`}>
                                    {userSpendLimitChatText(userMonthToDate, userSpendLimit)}
                                </p>
                            </>
                        ) : pauseReason === 'annual_pool_exceeded' ||
                          pauseReason === 'no_credits' ? (
                            <>
                                <p className={`${textSize} font-semibold text-danger-800`}>
                                    {pauseReason === 'no_credits'
                                        ? 'No Studio AI credits available.'
                                        : 'Annual credit pool exhausted.'}
                                </p>
                                <p className={`mt-0.5 ${subTextSize} text-danger-600`}>
                                    DANI queries are paused. Contact your account team to add
                                    credits or upgrade your contract.
                                </p>
                            </>
                        ) : (
                            <>
                                <p className={`${textSize} font-semibold text-danger-800`}>
                                    DANI queries paused until {resetLabel}.
                                </p>
                                <p className={`mt-0.5 ${subTextSize} text-danger-600`}>
                                    Your monthly compute cap has been reached. Queries will resume
                                    when the cap resets.
                                </p>
                            </>
                        )}
                    </div>
                </div>
            )}

            {showWarnings && isDaniWarning && (
                <div
                    className={`flex items-start gap-3 rounded-xl border border-warning-200 bg-warning-50 ${padding}`}
                >
                    <Icon
                        icon="lucide:alert-triangle"
                        className="mt-0.5 shrink-0 text-warning"
                        width={iconSize}
                        height={iconSize}
                    />
                    <div className="min-w-0 flex-1">
                        <p className={`${textSize} font-semibold text-warning-800`}>
                            {formatCredits(creditsRemaining)} DANI credits remaining this month.
                        </p>
                        <p className={`mt-0.5 ${subTextSize} text-warning-600`}>
                            You&apos;ve used {Math.round(estimatedPct * 100)}% of your monthly
                            compute cap
                            {(summary?.daniCompute.estimatedPercentUsed ?? 0) >
                            (summary?.daniCompute.percentUsed ?? 0)
                                ? ' (estimated)'
                                : ''}
                            . Resets {resetLabel}.
                        </p>
                    </div>
                </div>
            )}

            {showWarnings && isPoolWarning && summary && (
                <div
                    className={`flex items-start gap-3 rounded-xl border border-warning-200 bg-warning-50 ${padding}`}
                >
                    <Icon
                        icon="lucide:trending-up"
                        className="mt-0.5 shrink-0 text-warning"
                        width={iconSize}
                        height={iconSize}
                    />
                    <div className="min-w-0 flex-1">
                        <p className={`${textSize} font-semibold text-warning-800`}>
                            Annual pool at {Math.round(poolPct * 100)}% consumed.
                        </p>
                        <p className={`mt-0.5 ${subTextSize} text-warning-600`}>
                            {formatCredits(summary.totalPool.creditsRemaining)} credits remaining in
                            your annual contract pool.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
